import { useState, useEffect, useContext, useLayoutEffect, useCallback, useRef } from 'react';
import { fromArrayBuffer } from 'geotiff';
import * as THREE from 'three';

import { DesignContext } from '../contexts/designContext.js';
import { withMeasuredDSMBias } from '../utils/dsmParams.js';

import { downloadCanvas } from '../algorithms/geometry.js';

import {
  getDisplacementBias,
  getOffset,
  getTiff,
  updateTextureData,
  fillPolygon,
  getPointClouds
} from '../algorithms/dsm.js';

const buildDSMUpdateKey = ({ dsmSrc, segments, trees, dsmOffset, biasOverride, rgbWidth, rgbHeight, resolution }) =>
  JSON.stringify({
    dsmSrc,
    dsmOffset,
    biasOverride,
    rgbWidth,
    rgbHeight,
    resolution,
    segments: (segments || []).map((segment) => ({
      geometry: segment?.geometry || []
    })),
    trees: (trees || []).map((tree) => ({
      dsm: tree?.dsm || null,
      geometry: tree?.geometry || []
    }))
  });

export const useDSM = (canvasPool, dsmSrc, segments, trees, updateLoadingStatus, textureOrSize = null) => {
  const { dsmParams, setDSMParams, resolution } = useContext(DesignContext);

  const isLoadingDSMRef = useRef(false);
  const tmDSMRef = useRef(0);
  const lastUpdateKeyRef = useRef(null);
  const missingDSMResetRef = useRef(false);

  const rgbWidth = typeof textureOrSize === 'number' && textureOrSize > 0 ? textureOrSize : (textureOrSize?.image?.width ?? (textureOrSize?.width ?? null));
  const rgbHeight = typeof textureOrSize === 'number' && textureOrSize > 0 ? textureOrSize : (textureOrSize?.image?.height ?? (textureOrSize?.height ?? null));
  const existingBias = Number(dsmParams?.bias);
  const hasFallbackBiasOverride = dsmParams?.biasSource === 'fallback';
  const biasOverride = Number.isFinite(existingBias) && existingBias >= 0 && !hasFallbackBiasOverride ? existingBias : null;

  const [[raster, rasterHeight, rasterWidth], setRaster] = useState([null, 0, 0]);

  const [[orgTexture, orgDSMTexture, dsmTexture, minDSM, maxDSM, displacementBias], setDSMTexture] = useState([
    null,
    null,
    null,
    0,
    255,
    0
  ]);
  const [loadStatus, setLoadStatus] = useState('DSM.Loading');
  const [dsmOffset, setDSMOffset] = useState([0, 0]);

  const changeLoadStatus = useCallback(
    (status) => {
      setLoadStatus(status);
      updateLoadingStatus(status);
    },
    [updateLoadingStatus]
  );

  const changeOffset = (offset) => {
    setDSMOffset(offset);
    setDSMParams({ dsmOffset: offset, bias: displacementBias });
  };

  const calculateAndSetOffset = (borders, resolution, scale, center) => {
    const offset = getOffset(raster, rasterHeight, rasterWidth, borders, resolution, scale, center, rgbWidth, rgbHeight);
    changeOffset(offset);
    return offset;
  };

  useEffect(() => {
    if (
      dsmParams &&
      dsmParams.dsmOffset &&
      (dsmParams.dsmOffset[0] !== dsmOffset[0] || dsmParams.dsmOffset[1] !== dsmOffset[1])
    ) {
      const offset = [dsmParams.dsmOffset[0], dsmParams.dsmOffset[1]];
      const [ox, oy] = offset;
      dsmOffset[0] = ox;
      dsmOffset[1] = oy;
      setDSMOffset(offset);
    }
  }, [dsmOffset, dsmParams]);

  const getDSMData = useCallback(() => {
    const rasterData = [raster, rasterHeight, rasterWidth];
    const dsmTextureData = [orgTexture, orgDSMTexture, dsmTexture, minDSM, maxDSM, displacementBias];
    const dsmParamsData = { bias: displacementBias, dsmOffset };
    const dsmData = {
      rasterData,
      dsmTextureData,
      dsmParamsData,
      rgbWidth,
      rgbHeight,
      resolution
    };
    return dsmData;
  }, [
    displacementBias,
    dsmOffset,
    dsmTexture,
    maxDSM,
    minDSM,
    orgDSMTexture,
    orgTexture,
    raster,
    rasterHeight,
    rasterWidth,
    resolution,
    rgbHeight,
    rgbWidth
  ]);

  const loadedHandler = useCallback(
    (dsmData) => {
      if (!dsmData) return;
      const { rasterData, dsmTextureData, dsmParamsData } = dsmData;
      setRaster(rasterData);
      setDSMTexture(dsmTextureData);
      const measuredParams = withMeasuredDSMBias(dsmParams, dsmParamsData.bias, dsmParamsData.dsmOffset);
      if (
        measuredParams &&
        (Number(dsmParams?.bias) !== measuredParams.bias ||
          dsmParams?.biasSource === 'fallback' ||
          dsmParams?.dsmOffset !== measuredParams.dsmOffset)
      ) {
        if (setDSMParams) setDSMParams(measuredParams);
      }
      changeLoadStatus('DSM.Loaded');
      isLoadingDSMRef.current = false;
      lastUpdateKeyRef.current = null;
    },
    [canvasPool, changeLoadStatus, dsmParams, rgbHeight, rgbWidth, setDSMParams]
  );

  const errorHandler = useCallback(() => {
    changeLoadStatus('DSM.Failed');
    isLoadingDSMRef.current = false;
  }, [changeLoadStatus]);

  const loadedForDsmSrcRef = useRef(null);

  const loadDSM = useCallback(() => {
    if (!isLoadingDSMRef.current && loadStatus !== 'DSM.Failed') {
      const dsmSrcChanged = loadedForDsmSrcRef.current !== dsmSrc;
      const needsFetch = !orgTexture || dsmSrcChanged;

      if (needsFetch && dsmSrc) {
        if (dsmSrcChanged) {
          setDSMTexture([null, null, null, 0, 255, 0]);
          setRaster([null, 0, 0]);
          setLoadStatus('DSM.Loading');
          lastUpdateKeyRef.current = null;
        }
        isLoadingDSMRef.current = true;
        const wrappedLoadedHandler = (dsmData) => {
          loadedHandler(dsmData);
          loadedForDsmSrcRef.current = dsmSrc;
        };
        getTiff(
          canvasPool,
          segments,
          trees,
          dsmSrc,
          dsmOffset,
          wrappedLoadedHandler,
          errorHandler,
          rgbWidth,
          rgbHeight,
          biasOverride,
          resolution
        );
      }
    }
  }, [
    loadStatus,
    orgTexture,
    canvasPool,
    segments,
    trees,
    dsmSrc,
    dsmOffset,
    loadedHandler,
    errorHandler,
    rgbWidth,
    rgbHeight,
    biasOverride,
    resolution
  ]);

  useLayoutEffect(() => {
    loadDSM(dsmSrc);
  }, [dsmSrc, segments, trees, dsmOffset, loadDSM]);

  useEffect(() => {
    if (loadStatus !== 'DSM.Loaded') return;
    if (!orgTexture?.image) return;

    const key = buildDSMUpdateKey({
      dsmSrc,
      segments,
      trees,
      dsmOffset,
      biasOverride,
      rgbWidth,
      rgbHeight,
      resolution
    });
    if (lastUpdateKeyRef.current === key) return;

    clearTimeout(tmDSMRef.current);
    tmDSMRef.current = setTimeout(() => {
      const dsmData = getDSMData();
      const [orgDSM] = dsmData.dsmTextureData ?? [];
      if (!orgDSM?.image) return;

      const newTextureData = updateTextureData(
        canvasPool,
        {
          ...dsmData,
          dsmParamsData: {
            ...dsmData.dsmParamsData,
            bias: biasOverride ?? dsmData.dsmParamsData?.bias
          },
          rgbWidth,
          rgbHeight
        },
        segments,
        trees
      );
      setDSMTexture(newTextureData);

      const bias = newTextureData?.[5];
      const measuredParams = withMeasuredDSMBias(dsmParams, bias, dsmOffset);
      if (
        measuredParams &&
        (biasOverride === null || Number(biasOverride) !== measuredParams.bias || hasFallbackBiasOverride)
      ) {
        setDSMParams(measuredParams);
      }

      lastUpdateKeyRef.current = key;
    }, 250);

    return () => clearTimeout(tmDSMRef.current);
  }, [
    canvasPool,
    dsmOffset,
    dsmSrc,
    getDSMData,
    loadStatus,
    orgTexture,
    rgbHeight,
    rgbWidth,
    resolution,
    segments,
    setDSMParams,
    trees,
    biasOverride,
    dsmParams,
    hasFallbackBiasOverride
  ]);

  useEffect(() => {
    if (dsmSrc) {
      missingDSMResetRef.current = false;
      return;
    }
    if (missingDSMResetRef.current) return;
    missingDSMResetRef.current = true;
    loadedForDsmSrcRef.current = null;
    isLoadingDSMRef.current = false;
    lastUpdateKeyRef.current = null;
    setRaster([null, 0, 0]);
    setDSMTexture([null, null, null, 0, 255, 0]);
    if (loadStatus !== 'DSM.Failed') changeLoadStatus('DSM.Failed');
  }, [dsmSrc, loadStatus, changeLoadStatus]);

  return {
    orgDSMTexture,
    dsmTexture,
    minDSM,
    maxDSM,
    displacementBias,
    dsmOffset,
    loadStatus,
    dsmRaster: raster,
    dsmRasterHeight: rasterHeight,
    dsmRasterWidth: rasterWidth,
    fillPolygon,
    getPointClouds,
    calculateAndSetOffset,
    changeOffset,
    getDSMData
  };
};

export default useDSM;
